import { TWorkspaceMemberRole } from '@/features/user-settings/types/workspace-types';

import { APP_ROUTES } from '@/shared/constants/navigation';
import { QUERY_PARAMS } from '@/shared/constants/query-params';
import { getCustomerPortalLink } from '@/shared/services/payment';

import { redirectViaWindow } from './window';

export async function goToStripeCustomerPortal(
	pageId: string,
	isOverdue?: boolean,
): Promise<boolean> {
	try {
		const res = await getCustomerPortalLink(pageId, isOverdue);
		if (res.url) {
			// Same tab, not a popup: this runs after an awaited fetch, so the click's
			// user-gesture token is already spent and `window.open` gets blocked.
			redirectViaWindow(res.url);
			return true;
		}

		return false;
	} catch (err) {
		console.error(`Error fetching customer portal link`, err);
		return false;
	}
}

export function isUserAllowedToPurchase(role?: TWorkspaceMemberRole) {
	return !!role;
}

function withPageId(route: string, pageId?: string | null): string {
	return pageId ? `${route}?${QUERY_PARAMS.PAGE_ID}=${pageId}` : route;
}

export function getUsageDestination(pageId?: string | null): string {
	return withPageId(APP_ROUTES.BILLING, pageId);
}

/**
 * Where an upgrade prompt should send the user.
 *
 * Billing is only useful to someone who already has a subscription to top up — it is
 * where extra credits live. A Free workspace sent there lands on a page with no top-up
 * controls and no way to buy a plan, so every other case goes to Pricing.
 *
 * `isCreditsNudge` distinguishes "you ran out of monthly credits" (buy more usage)
 * from a feature gate like "analytics needs a paid plan" (buy a different plan).
 *
 * Role does not change the destination: every non-referrer can open Billing, and its
 * admin-only controls are shown locked there rather than hidden.
 */
export function getUpgradeDestination({
	isCreditsNudge,
	isFreePlan,
	pageId,
}: {
	isCreditsNudge: boolean;
	isFreePlan: boolean;
	pageId?: string | null;
}): string {
	return withPageId(isCreditsNudge && !isFreePlan ? APP_ROUTES.BILLING : APP_ROUTES.PLANS, pageId);
}
